import { useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

export const SUPPORTED_OAUTH_PROVIDERS = ['google', 'github'] as const

export type OAuthProvider = (typeof SUPPORTED_OAUTH_PROVIDERS)[number]

function isSupportedProvider(value: string | null): value is OAuthProvider {
  return SUPPORTED_OAUTH_PROVIDERS.includes(value as OAuthProvider)
}

/**
 * Starts an OAuth login from a `?provider=` link, so the state nonce is written on
 * the same origin that reads it back on the callback.
 */
export default function useProviderLoginFromQuery(
  startLogin: (provider: OAuthProvider) => void,
): void {
  const { pathname, search } = useLocation()
  const history = useHistory()
  const startedRef = useRef(false)

  useEffect(() => {
    // Guarded against a second run — StrictMode's double mount would fetch a
    // second redirect_url and overwrite the state the first one stored.
    if (startedRef.current) return

    const params = new URLSearchParams(search)
    const provider = params.get('provider')
    // The value is interpolated into an API URL, so only the known two pass.
    if (!isSupportedProvider(provider)) return

    startedRef.current = true

    // Drop the param first so the back button can't relaunch the flow.
    params.delete('provider')
    const remaining = params.toString()
    history.replace(remaining.length > 0 ? `${pathname}?${remaining}` : pathname)

    startLogin(provider)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
